import React, { useState, useEffect, useContext } from "react";
import { Route, Switch, Redirect } from "react-router-dom";
import "./App.css";
import MainLayout from "./layouts/MainLayout";
import { AuthContext } from "./context/AuthContext";
import { routes, hasRouteAccess } from "./routes/routes";

function App() {
  const { user, token, loading } = useContext(AuthContext);

  // Show loading while auth state is being restored
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
          fontSize: "18px",
          fontWeight: "600",
        }}
      >
        Loading...
      </div>
    );
  }

  const isAuthenticated = !!token && !!user;

  return (
    <Switch>
      {/* ============================================
          AUTH ROUTES (Login, Forgot Password)
          ============================================ */}
      {routes.auth.map((route) => (
        <Route key={route.path} exact={route.exact} path={route.path}>
          {isAuthenticated ? <Redirect to="/dashboard" /> : <route.component />}
        </Route>
      ))}

      {/* ============================================
          PROTECTED ROUTES (User must be authenticated)
          ============================================ */}
      {routes.protected.map((route) => (
        <Route key={route.path} path={route.path}>
          {isAuthenticated ? (
            // Check role-based access
            hasRouteAccess(route.path, user.role_id) ? (
              // For some routes (e.g. outlet live session) we want a full-screen view
              // without sidebar/top bar.
              (() => {
                const isFullScreenRoute =
                  route.path === "/outlet-live-session" ||
                  route.path === "/mobile-live-session" ||
                  route.path === "/all-draw";
                return (
                  <MainLayout
                    pageTitle={route.name}
                    showSidebar={!isFullScreenRoute}
                    showTopBar={!isFullScreenRoute}
                    showFooter={false}
                  >
                    <route.component
                      userId={user.user_id}
                      username={user.username}
                      userRole={user.role_id}
                      user={user}
                    />
                  </MainLayout>
                );
              })()
            ) : (
              // User doesn't have access to this route
              <Redirect to="/dashboard" />
            )
          ) : (
            // User not authenticated
            <Redirect to="/login" />
          )}
        </Route>
      ))}

      {/* ============================================
          PUBLIC ROUTES (Accessible to everyone)
          ============================================ */}
      {routes.public.map((route) => (
        <Route key={route.path} path={route.path}>
          <MainLayout
            pageTitle={route.name}
            showSidebar={isAuthenticated}
            showTopBar={isAuthenticated}
            showFooter={true}
          >
            <route.component />
          </MainLayout>
        </Route>
      ))}

      {/* ============================================
          404 FALLBACK (Redirect to appropriate page)
          ============================================ */}
      <Route path="*">
        <Redirect to={isAuthenticated ? "/dashboard" : "/login"} />
      </Route>
    </Switch>
  );
}

export default App;
